// Debuff uptime panel — Phase 2A.4.
//
// One row per tracked raid debuff. Shows total uptime% across the analysed
// window plus, for stacked debuffs (Sunder Armor), the time at full stacks.

import type { DebuffSummary, DebuffUptime } from '@shared/analysis';
import { t } from '@shared/i18n';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

const ICON_BASE = '/api/img/images/wow/icons/medium';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function fmtPct(p: number): string {
  if (!Number.isFinite(p)) return '–';
  return `${(p * 100).toFixed(0)}%`;
}

function fmtSec(ms: number): string {
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

function flavour(p: number): string {
  if (p >= 0.95) return 'good';
  if (p < 0.6) return 'bad';
  return '';
}

function makeRow(d: DebuffUptime, fightDurMs: number): HTMLTableRowElement {
  const tr = document.createElement('tr');
  const upPct = fightDurMs > 0 ? d.uptimeMs / fightDurMs : 0;
  const fullPct = (d.fullStackUptimeMs != null && fightDurMs > 0)
    ? d.fullStackUptimeMs / fightDurMs
    : null;
  const fullCell = fullPct == null
    ? '<td class="num muted">–</td>'
    : `<td class="num ${flavour(fullPct)}">${fmtPct(fullPct)} <span class="muted small">(${d.fullStackThreshold}+)</span></td>`;
  tr.innerHTML = `
    <td class="ability">
      <a class="db-link" target="_blank" rel="noopener" href="/api/wh/classic/spell=${d.primaryAbilityID}">
        <span class="icon-slot"></span>
        <span class="db-name">spell ${d.primaryAbilityID}</span>
      </a>
    </td>
    <td class="num ${flavour(upPct)}">${fmtPct(upPct)}</td>
    ${fullCell}
    <td class="num muted">${fmtSec(d.uptimeMs)} <span class="small">/ ${fmtSec(fightDurMs)}</span></td>
    <td class="bar-col"><div class="db-bar" style="width:${Math.min(100, upPct * 100).toFixed(1)}%"></div></td>
  `;

  const link = tr.querySelector<HTMLAnchorElement>('a.db-link');
  if (link) attachTooltip(link, 'spell', d.primaryAbilityID);

  void lookup('spell', d.primaryAbilityID).then((r) => {
    if (r.name) {
      const n = tr.querySelector<HTMLSpanElement>('.db-name');
      if (n) n.textContent = r.name;
    }
    const slot = tr.querySelector<HTMLSpanElement>('.icon-slot');
    if (!slot) return;
    const iconSrc = r.icon ? iconNameToUrl(r.icon) : `${ICON_BASE}/inv_misc_questionmark.jpg`;
    const img = document.createElement('img');
    img.className = 'icon';
    img.width = 20;
    img.height = 20;
    img.alt = '';
    img.src = iconSrc;
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
    slot.replaceChildren(img);
  });

  return tr;
}

export function renderDebuffPanel(
  d: DebuffSummary,
  container: HTMLElement,
): void {
  container.replaceChildren();

  if (d.debuffs.length === 0) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent = t('debuff.empty');
    container.appendChild(p);
    return;
  }

  const summary = document.createElement('p');
  summary.className = 'analysis-summary muted';
  summary.textContent = t('debuff.summary', { dur: fmtSec(d.fightDurationMs) });
  container.appendChild(summary);

  const table = document.createElement('table');
  table.className = 'db-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('debuff.col.name')}</th>
        <th title="${escapeHtml(t('debuff.col.uptimeTip'))}">${t('debuff.col.uptime')}</th>
        <th title="${escapeHtml(t('debuff.col.fullStackTip'))}">${t('debuff.col.fullStack')}</th>
        <th>${t('debuff.col.duration')}</th>
        <th class="bar-col"></th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;
  for (const dd of d.debuffs) tbody.appendChild(makeRow(dd, d.fightDurationMs));
  container.appendChild(table);
}
